import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import heroBg from "../assets/images/HeroBanner.png";
import "../styles/WFOTPayment.css";
import {
  loadRazorpayScript,
  createWfotOrder,
  openRazorpayCheckout,
  verifyWfotPayment,
  formatIndianCurrency
} from "../utils/razorpay";

const WFOTPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const baseUrl = process.env.REACT_APP_BASEURL;

  const wfotData = location.state?.wfotData;

  useEffect(() => {
    if (!wfotData) {
      navigate('/wfot-form');
    }
  }, [wfotData, navigate]);

  const calculatePaymentBreakdown = () => {
    const baseAmount = 2500; // Fixed amount for WFOT
    const gstAmount = Math.round((baseAmount * 18) / 118); // Since amount includes GST
    const subtotal = baseAmount - gstAmount;

    return {
      subtotal,
      gstAmount,
      totalAmount: baseAmount
    };
  };

  const handleProceedToPayment = async () => {
    try {
      await loadRazorpayScript();
      setLoading(true);
      setError(null);

      const breakdown = calculatePaymentBreakdown();

      const orderResp = await createWfotOrder({
        baseUrl,
        amountInRupees: breakdown.totalAmount,
        wfotId: wfotData._id,
        notes: {
          breakdown: JSON.stringify(breakdown)
        }
      });

      await openRazorpayCheckout({
        order: orderResp.order,
        name: "AIOTA - WFOT Application",
        description: "WFOT Individual Membership Application Fee",
        prefill: {
          name: wfotData.name || "",
          email: wfotData.email || "",
          contact: wfotData.phone || ""
        },
        notes: {
          module: 'WFOT_FORM',
          wfot_id: wfotData._id
        },
        onSuccess: async (response) => {
          try {
            console.log("Payment successful, verifying payment...");
            
            const verifyResp = await verifyWfotPayment({
              baseUrl,
              payload: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                amount_paise: breakdown.totalAmount * 100,
                amount_rupees: breakdown.totalAmount
              },
              wfotId: wfotData._id
            });

            console.log("Payment verification response:", verifyResp);

            if (verifyResp?.success) {
              // Redirect to success page
              navigate('/wfot-payment-success', {
                state: {
                  wfotData: {
                    name: wfotData.name,
                    email: wfotData.email,
                    phone: wfotData.phone,
                    place_of_work: wfotData.place_of_work
                  },
                  paymentDetails: {
                    orderId: response.razorpay_order_id,
                    paymentId: response.razorpay_payment_id,
                    amount: breakdown.totalAmount * 100,
                    amountInRupees: breakdown.totalAmount
                  }
                }
              });
            } else {
              throw new Error("Payment verification failed");
            }
          } catch (err) {
            console.error("Payment verification error:", err);
            setError("Payment verification failed. Please contact support with your payment details.");
          }
        },
        onFailure: (err) => {
          console.error("Payment failed:", err);
          setError(err?.message || "Payment was not completed.");
        }
      });
    } catch (err) {
      console.error("Payment initialization error:", err);
      setError("Unable to initiate payment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!wfotData) {
    return null;
  }

  const breakdown = calculatePaymentBreakdown();

  return (
    <>
      {/* Hero Section */}
      <section
        className="hero-fixed-wfot-payment"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="hero-content-wfot-payment">
          <h1 className="hero-title">All India Occupational Therapists' Association</h1>
        </div>
      </section>

      {/* Scrollable Content */}
      <div className="scrollable-content scrollable-content-wfot-payment">
        <div className="content-container wfot-payment-content-container">
          <section className="wfot-payment-section">
            <Container className="container-wfot-payment">
              <h2 className="text-center wfot-payment-heading mb-4">WFOT Application Payment</h2>

              <Row className="justify-content-center">
                <Col md={8}>
                  <Card className="payment-card">
                    <Card.Body className="p-4">
                      <div className="payment-header text-center mb-4">
                        <h3>WFOT Individual Membership Application</h3>
                        <p className="text-muted">Complete your application by making the payment</p>
                      </div>

                      <div className="member-info mb-4">
                        <h5 className="mb-3">Application Details</h5>
                        <div className="info-row">
                          <span className="label">Name:</span>
                          <span className="value">{wfotData.name}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Email:</span>
                          <span className="value">{wfotData.email}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Phone:</span>
                          <span className="value">{wfotData.phone}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Place of Work:</span>
                          <span className="value">{wfotData.place_of_work}</span>
                        </div>
                      </div>

                      <div className="payment-breakdown mb-4">
                        <h5 className="mb-3">Payment Breakdown</h5>
                        <div className="breakdown-table">
                          <div className="breakdown-row">
                            <span className="label">WFOT Application Fee:</span>
                            <span className="value">{formatIndianCurrency(breakdown.subtotal)}</span>
                          </div>
                          <div className="breakdown-row">
                            <span className="label">GST (18%):</span>
                            <span className="value">{formatIndianCurrency(breakdown.gstAmount)}</span>
                          </div>
                          <div className="breakdown-row total">
                            <span className="label">Total Amount:</span>
                            <span className="value">{formatIndianCurrency(breakdown.totalAmount)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="payment-notes mb-4">
                        <div className="note warning">
                          <h6 className="mb-3">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Important Notes:
                          </h6>
                          <ul className="mb-0">
                            <li>The application fee includes 18% GST</li>
                            <li>Once submitted, the application fee is non-refundable</li>
                            <li>In case of technical errors or network issues during payment, the amount will be automatically refunded by Razorpay</li>
                            <li>Please ensure stable internet connection before proceeding with the payment</li>
                            <li>After successful payment, your application will be submitted for secretary approval</li>
                            <li>Your WFOT certificate will be issued after approval from the secretary</li>
                          </ul>
                        </div>
                      </div>

                      {error && (
                        <Alert variant="danger" className="mb-4">
                          <h6 className="mb-2">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Payment Error
                          </h6>
                          <p className="mb-2">{error}</p>
                          <p className="mb-0 small">
                            If this issue persists, please contact support with your application details.
                          </p>
                        </Alert>
                      )}

                      <div className="payment-actions text-center">
                        <button 
                          className="back-button me-3"
                          onClick={() => navigate('/')}
                          disabled={loading}
                        >
                          Back to Form
                        </button>
                        <button
                          className="payment-button"
                          onClick={handleProceedToPayment}
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Proceed to Payment"}
                        </button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </>
  );
};

export default WFOTPayment;
